import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Connects to data-controller="ui--ai-field-helper"
// AI-powered field helper that calls the field-actions API
// and shows a preview with Accept/Discard options.
export default class extends Controller {
  static targets = [
    "trigger",
    "menu",
    "backdrop",
    "preview",
    "previewLabel",
    "previewText",
    "loading",
    "error",
    "errorText",
  ]

  static values = {
    targetInputId: String,
    fieldType: { type: String, default: "textarea" },
    targetLanguage: { type: String, default: "English" },
    effort: { type: String, default: "" },
  }

  get #effortStorageKey() {
    const match = window.location.pathname.match(/^\/s:([^:/]+):/)
    const spaceKey = match?.[1] ?? "default"
    return `ai-effort-level:${spaceKey}`
  }

  connect() {
    this.escapeHandler = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.escapeHandler)

    // Initialize effort from localStorage (namespaced by space group)
    const storedEffort = localStorage.getItem(this.#effortStorageKey)
    if (storedEffort) {
      this.effortValue = storedEffort
    }

    // Fetch and render effort levels
    this.#loadEffortLevels()
  }

  disconnect() {
    document.removeEventListener("keydown", this.escapeHandler)
  }

  toggleMenu(event) {
    event.stopPropagation()

    if (this.isMenuOpen()) {
      this.closeMenu()
    } else {
      this.openMenu()
    }
  }

  openMenu() {
    if (this.hasMenuTarget && this.hasBackdropTarget) {
      this.menuTarget.classList.remove("hidden")
      this.backdropTarget.classList.remove("hidden")
    }
  }

  closeMenu() {
    if (this.hasMenuTarget && this.hasBackdropTarget) {
      this.menuTarget.classList.add("hidden")
      this.backdropTarget.classList.add("hidden")
    }
  }

  isMenuOpen() {
    return this.hasMenuTarget && !this.menuTarget.classList.contains("hidden")
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      if (this.isMenuOpen()) {
        this.closeMenu()
      } else if (this.isPreviewVisible()) {
        this.discard()
      }
    }
  }

  async performAction(event) {
    const action = event.currentTarget.dataset.aiAction
    if (!action) return

    this.closeMenu()
    this.hideError()
    this.hidePreview()
    this.showLoading()

    const inputElement = document.getElementById(this.targetInputIdValue)
    if (!inputElement) {
      this.showError("Target input field not found")
      this.hideLoading()
      return
    }

    const value = inputElement.value || inputElement.textContent || ""
    if (!value.trim()) {
      this.showError("Field is empty — enter some text first")
      this.hideLoading()
      return
    }

    try {
      const response = await post("/api/v1/ai/field-actions", {
        body: {
          action_type: action,
          field_type: this.fieldTypeValue,
          value: value,
          target_language: this.targetLanguageValue,
          effort: this.effortValue,
        },
        contentType: "application/json",
        responseKind: "json",
        credentials: "include",
      })

      this.hideLoading()

      if (response.ok) {
        const data = await response.json
        this.showPreview(action, data.result)
      } else {
        const errorData = await response.json
        this.showError(errorData?.error || "AI action failed")
      }
    } catch (_err) {
      this.hideLoading()
      this.showError("Network error — please try again")
    }
  }

  accept() {
    const inputElement = document.getElementById(this.targetInputIdValue)
    if (!inputElement || !this.hasPreviewTextTarget) return

    const newValue = this.previewTextTarget.textContent || ""
    inputElement.value = newValue

    // Dispatch input event so Rails form helpers and other listeners notice the change
    inputElement.dispatchEvent(new Event("input", { bubbles: true }))
    inputElement.dispatchEvent(new Event("change", { bubbles: true }))

    this.hidePreview()
  }

  discard() {
    this.hidePreview()
  }

  // Private helpers

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }
    if (this.hasTriggerTarget) {
      this.triggerTarget.disabled = true
      this.triggerTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
    if (this.hasTriggerTarget) {
      this.triggerTarget.disabled = false
      this.triggerTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
  }

  showPreview(action, text) {
    if (
      this.hasPreviewTarget &&
      this.hasPreviewTextTarget &&
      this.hasPreviewLabelTarget
    ) {
      const labels = {
        improve: "Improved",
        expand: "Expanded",
        summarize: "Summarized",
        translate: "Translated",
        format: "Formatted",
      }
      this.previewLabelTarget.textContent = `AI Suggestion — ${labels[action] || action}`
      this.previewTextTarget.textContent = text
      this.previewTarget.classList.remove("hidden")
    }
  }

  hidePreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("hidden")
    }
  }

  isPreviewVisible() {
    return (
      this.hasPreviewTarget && !this.previewTarget.classList.contains("hidden")
    )
  }

  showError(message) {
    if (this.hasErrorTarget && this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden")
    }
  }

  selectEffort(event) {
    const slug = event.currentTarget.dataset.slug
    this.effortValue = slug
    localStorage.setItem(this.#effortStorageKey, slug)

    // Update pill active states
    this.element.querySelectorAll(".ai-effort-pill").forEach((pill) => {
      const isActive = pill.dataset.slug === slug
      pill.className = `ai-effort-pill px-2 py-0.5 text-xs rounded-full transition-colors ${isActive ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`
    })
  }

  async #loadEffortLevels() {
    try {
      const { get } = await import("@rails/request.js")
      const response = await get("/api/v1/ai/effort-levels", {
        credentials: "include",
        responseKind: "json",
      })
      if (response.ok) {
        const data = await response.json
        const levels = data.effort_levels || []
        if (levels.length > 1) {
          this.#renderEffortPills(levels)
          // Set default if not already stored
          if (!this.effortValue && levels.length > 0) {
            const defaultLevel = levels.find((l) => l.is_default) || levels[0]
            this.effortValue = defaultLevel.slug
            localStorage.setItem(this.#effortStorageKey, defaultLevel.slug)
          }
        }
      }
    } catch (_e) {
      // Silently fail — effort levels are optional UI
    }
  }

  #renderEffortPills(levels) {
    // Find or create the effort container
    let container = this.element.querySelector("[data-effort-pills]")
    if (!container) return // No designated slot in HTML

    container.innerHTML = levels
      .map(
        (level) => `
      <button type="button"
        class="ai-effort-pill px-2 py-0.5 text-xs rounded-full transition-colors ${level.slug === this.effortValue ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}"
        data-slug="${this.#escapeHtml(level.slug)}"
        data-action="click->ui--ai-field-helper#selectEffort"
        title="${this.#escapeHtml(level.description || level.name)}">
        ${this.#escapeHtml(level.name)}
      </button>
    `,
      )
      .join("")

    container.classList.remove("hidden")
  }

  #escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = String(text)
    return div.innerHTML
  }
}
